import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from "react-router-dom";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { ArrowRight } from "lucide-react";
import "@/App.css";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdminRoot } from "@/admin/AdminRoot";
import { Home } from "@/pages/Home";
import { WhyUniversHeal } from "@/pages/WhyUniversHeal";
import { MedicalCare } from "@/pages/MedicalCare";
import { Wellness } from "@/pages/Wellness";
import { Ayurveda } from "@/pages/Ayurveda";
import { Destinations, DestinationDetail } from "@/pages/Destinations";
import { HowItWorks } from "@/pages/HowItWorks";
import { WhyKerala } from "@/pages/WhyKerala";
import { YourJourney } from "@/pages/YourJourney";
import { Specialties } from "@/pages/Specialties";
import { SpecialtyDetail } from "@/pages/SpecialtyDetail";
import { Hospitals } from "@/pages/Hospitals";
import { HospitalDetail } from "@/pages/HospitalDetail";
import { GetMedicalOpinion } from "@/pages/GetMedicalOpinion";
import { SecondOpinion } from "@/pages/SecondOpinion";
import { TreatmentPlanning } from "@/pages/TreatmentPlanning";
import { Faq } from "@/pages/Faq";
import { About } from "@/pages/About";
import { Contact } from "@/pages/Contact";
import { PrivacyPolicy } from "@/pages/PrivacyPolicy";
import { TermsOfUse } from "@/pages/TermsOfUse";
import { MedicalDisclaimer } from "@/pages/MedicalDisclaimer";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, info) {
        console.error(error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-ivory px-6 text-center">
                    <div>
                        <p className="font-serif text-3xl text-forest">
                            Something went wrong.
                        </p>
                        <p className="mt-3 text-moss">
                            Please refresh the page. Our team has been notified.
                        </p>
                        <a href="/" className="btn-primary mt-8 inline-flex">
                            Back to Home
                        </a>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.__lenis) {
            window.__lenis.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo(0, 0);
        }
    }, [pathname]);
    return null;
};

// Subtle cross-fade page transition.
const AnimatedRoutes = () => {
    const location = useLocation();
    return (
        <motion.main
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="flex-1"
        >
            <Routes location={location}>
                <Route path="/" element={<Home />} />
                <Route path="/why-universheal" element={<WhyUniversHeal />} />
                {/* Legacy brand route — redirect any indexed /why-healease links */}
                <Route path="/why-healease" element={<Navigate to="/why-universheal" replace />} />
                <Route path="/medical-care" element={<MedicalCare />} />
                <Route path="/wellness" element={<Wellness />} />
                <Route path="/ayurveda" element={<Ayurveda />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/why-kerala" element={<WhyKerala />} />
                <Route path="/your-journey" element={<YourJourney />} />
                <Route path="/specialties" element={<Specialties />} />
                <Route path="/specialties/:slug" element={<SpecialtyDetail />} />
                <Route path="/hospitals" element={<Hospitals />} />
                <Route path="/hospitals/:slug" element={<HospitalDetail />} />
                <Route path="/destinations" element={<Destinations />} />
                <Route path="/destinations/:slug" element={<DestinationDetail />} />
                <Route
                    path="/get-a-medical-opinion"
                    element={<GetMedicalOpinion />}
                />
                <Route path="/second-opinion" element={<SecondOpinion />} />
                <Route
                    path="/treatment-planning"
                    element={<TreatmentPlanning />}
                />
                <Route path="/faq" element={<Faq />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route
                    path="/medical-disclaimer"
                    element={<MedicalDisclaimer />}
                />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </motion.main>
    );
};

const NotFound = () => {
    const { t } = useLang();
    return (
    <div className="pt-40 pb-32 text-center container-site">
        <p className="eyebrow text-moss">{t("common.notFound.eyebrow", "404 — Page not found")}</p>
        <h1 className="font-serif text-5xl mt-5 text-forest">
            {t("common.notFound.title", "The page you are looking for doesn't exist.")}
        </h1>
        <Link to="/" data-testid="not-found-home" className="btn-primary mt-10 inline-flex group">
            {t("common.notFound.back", "Back to Home")}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
    </div>
    );
};

const AppShell = () => {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/admin");

    useEffect(() => {
        if (isAdmin || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, [isAdmin]);

    // The operations console is a standalone staff tool — no public header/footer,
    // English-only (not translated).
    if (isAdmin) return <AdminRoot />;

    return (
        <LanguageProvider>
            <div className="grain min-h-screen flex flex-col bg-ivory">
                <Header />
                <AnimatedRoutes />
                <Footer />
            </div>
        </LanguageProvider>
    );
};

function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <ScrollToTop />
                <AppShell />
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
